import React, { useState, useCallback, useEffect, useRef } from "react";
import { playRight, playWrong } from "@/lib/feedbackSounds";
import { SoundToggle } from "@/components/SoundToggle";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Volume2, Check, X, RotateCcw, Zap, Play, Pause, Timer, Clock, Flame, Trophy, Share2, Crown, Target, CalendarDays, Images, ChevronRight } from "lucide-react";
import confetti from "canvas-confetti";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { StaffViewer } from "@/components/StaffViewer";
import { Button } from "@/components/ui/button";
import { playPitch, startMetronome, stopMetronome } from "@/lib/audio";
import { LETTERS } from "@/lib/music";
import { TRAINER_POOL as POOL } from "@/data/curriculum";
import { makeBadgeDataUrl, shareBadge, saveBadge, recordNote, readNoteStats, noteStatsFor } from "@/lib/trainerStore";
import { Link } from "react-router-dom";

function letterOf(pitch) {
  return pitch.replace(/[0-9]/g, "").replace(/[#b]/g, "");
}
// Difficulty controls the note range: Easy stays on the staff, Medium adds a
// few ledger notes, Hard extends well above and below with more ledger lines.
const DIFF_POOLS = {
  easy: {
    treble: ["E4", "F4", "G4", "A4", "B4", "C5", "D5", "E5", "F5"],
    bass: ["G2", "A2", "B2", "C3", "D3", "E3", "F3", "G3", "A3"],
  },
  medium: {
    treble: ["C4", "D4", "E4", "F4", "G4", "A4", "B4", "C5", "D5", "E5", "F5"],
    bass: ["E2", "F2", "G2", "A2", "B2", "C3", "D3", "E3", "F3", "G3", "A3"],
  },
  hard: {
    treble: ["A3", "B3", "C4", "D4", "E4", "F4", "G4", "A4", "B4", "C5", "D5", "E5", "F5", "G5", "A5", "B5", "C6"],
    bass: ["C2", "D2", "E2", "F2", "G2", "A2", "B2", "C3", "D3", "E3", "F3", "G3", "A3", "B3", "C4", "D4"],
  },
};
function randomPitch(clef, difficulty = "medium") {
  const pool = (DIFF_POOLS[difficulty] || DIFF_POOLS.medium)[clef] || POOL[clef];
  return pool[Math.floor(Math.random() * pool.length)];
}

function fmtTime(s) {
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}
const GOAL_OPTIONS = [5, 10, 20];
const todayStr = () => new Date().toISOString().slice(0, 10);

function readPractice() {
  try {
    return JSON.parse(localStorage.getItem("jt_practice") || "null");
  } catch (e) {
    return null;
  }
}

// Personal bests are stored locally, separately per difficulty level.
const PB_MIN_NOTES = 5; // accuracy only counts once a few notes are answered
const EMPTY_PB = { streak: 0, acc: 0, notes: 0 };
function readPB() {
  try {
    return JSON.parse(localStorage.getItem("jt_trainer_pb") || "null") || {};
  } catch (e) {
    return {};
  }
}
function pbFor(pb, diff) {
  return { ...EMPTY_PB, ...((pb && pb[diff]) || {}) };
}
function fireConfetti() {
  try {
    confetti({ particleCount: 90, spread: 70, origin: { y: 0.6 } });
  } catch (e) {}
}

function readGoals() {
  try {
    return JSON.parse(localStorage.getItem("jt_trainer_goals") || "null") || {};
  } catch (e) {
    return {};
  }
}
function goalsFor(g, diff) {
  return { streak: 0, acc: 0, ...((g && g[diff]) || {}) };
}

function NoteAccuracyMap({ stats, clef, difficulty }) {
  const map = noteStatsFor(stats, difficulty, clef);
  const pitches = (DIFF_POOLS[difficulty] || DIFF_POOLS.medium)[clef] || [];
  const seen = pitches.filter((p) => map[p] && map[p].attempts > 0);
  return (
    <div className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="note-accuracy-map">
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary"><Target size={16} /></span>
        <div>
          <p className="font-serif text-xl">Note accuracy map</p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground capitalize">{difficulty} · {clef} clef</p>
        </div>
      </div>
      {seen.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="note-map-empty">Answer some notes and your weak spots will light up here.</p>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-6 gap-2 sm:grid-cols-9" data-testid="note-map-grid">
            {pitches.map((p) => {
              const s = map[p];
              if (!s || !s.attempts) {
                return (
                  <div key={p} className="rounded-lg border border-dashed border-border py-2 text-center" data-testid={`note-cell-${p}`}>
                    <p className="font-serif text-lg text-muted-foreground/50">{p.replace(/[0-9]/g, "")}</p>
                    <p className="font-mono text-[9px] text-muted-foreground/40">—</p>
                  </div>
                );
              }
              const acc = Math.round((s.correct / s.attempts) * 100);
              const hue = Math.round((acc / 100) * 140); // 0 red -> 140 green
              return (
                <div
                  key={p}
                  data-testid={`note-cell-${p}`}
                  title={`${p}: ${acc}% over ${s.attempts}`}
                  className="rounded-lg py-2 text-center"
                  style={{ backgroundColor: `hsl(${hue} 55% 45% / 0.22)`, border: `1px solid hsl(${hue} 55% 45% / 0.5)` }}
                >
                  <p className="font-serif text-lg text-foreground">{p.replace(/[0-9]/g, "")}<span className="text-[10px] text-muted-foreground">{p.replace(/\D/g, "")}</span></p>
                  <p className="font-mono text-[9px] text-muted-foreground">{acc}%</p>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Greener = you read it well; redder = practice it more. Based on your answers on this device.</p>
        </>
      )}
    </div>
  );
}

function TrainerLeaderboard({ activeDifficulty, refreshKey }) {
  const [diff, setDiff] = useState(activeDifficulty || "medium");
  const [scope, setScope] = useState("weekly");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setDiff(activeDifficulty || "medium");
  }, [activeDifficulty]);
  useEffect(() => {
    let live = true;
    setLoading(true);
    api
      .get("/trainer/leaderboard", { params: { difficulty: diff, scope } })
      .then((r) => live && setData(r.data))
      .catch(() => {})
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [diff, scope, refreshKey]);
  const entries = data?.entries || [];
  return (
    <div className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="trainer-leaderboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary"><Crown size={16} /></span>
          <div>
            <p className="font-serif text-xl">Leaderboard</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{scope === "all" ? "All time" : "This week"} · ranked by best streak</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-full border border-border bg-background p-1">
            {[{ v: "weekly", l: "Weekly" }, { v: "all", l: "All-time" }].map((s) => (
              <button
                key={s.v}
                onClick={() => setScope(s.v)}
                data-testid={`leaderboard-scope-${s.v}`}
                className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${scope === s.v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                {s.l}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-full border border-border bg-background p-1">
            {["easy", "medium", "hard"].map((d) => (
              <button
                key={d}
                onClick={() => setDiff(d)}
                data-testid={`leaderboard-diff-${d}`}
                className={`rounded-full px-3 py-1 text-sm font-medium capitalize transition-colors ${diff === d ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      </div>
      {loading ? (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="leaderboard-loading">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="leaderboard-empty">No scores yet {scope === "all" ? "" : "this week"} — set a personal best and be the first!</p>
      ) : (
        <div className="mt-4 space-y-1.5" data-testid="leaderboard-list">
          {entries.map((e) => (
            <div
              key={e.rank}
              data-testid={`leaderboard-row-${e.rank}`}
              className={`flex items-center gap-3 rounded-xl px-3 py-2 ${e.is_me ? "bg-primary/10 ring-1 ring-primary/40" : "bg-background"}`}
            >
              <span className={`w-6 text-center font-serif text-lg ${e.rank <= 3 ? "text-primary" : "text-muted-foreground"}`}>{e.rank}</span>
              <span className="flex-1 truncate text-sm font-medium">{e.name}</span>
              <span className="flex items-center gap-1 text-sm text-foreground"><Zap size={13} className="text-primary" />{e.streak}</span>
              <span className="w-12 text-right text-sm text-muted-foreground">{e.accuracy}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PracticeTimer({ running, attempts, score, difficulty, sessionMaxStreak, pb, onCommitPB, onPostScore, onReset }) {
  const saved = readPractice();
  const isToday = saved && saved.date === todayStr();
  const [goalMin, setGoalMin] = useState(saved?.goalMin || 10);
  const [secs, setSecs] = useState(isToday ? saved.secs || 0 : 0);
  const [streak, setStreak] = useState(saved?.streak || 0);
  const [recap, setRecap] = useState(null);
  const [lbName, setLbName] = useState(() => {
    try { return localStorage.getItem("jt_trainer_name") || ""; } catch (e) { return ""; }
  });
  const [posting, setPosting] = useState(false);
  const [postedRank, setPostedRank] = useState(null);
  const metRef = useRef(isToday && (saved.secs || 0) >= (saved.goalMin || 10) * 60);
  const lastGoalDateRef = useRef(saved?.lastGoalDate || null);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") setSecs((s) => s + 1);
    }, 1000);
    return () => clearInterval(id);
  }, [running]);

  const resetTimer = () => {
    const notes = attempts;
    const acc = notes ? Math.round((score / notes) * 100) : 0;
    const best = pbFor(pb, difficulty);
    const records = {
      streak: sessionMaxStreak > best.streak,
      acc: notes >= PB_MIN_NOTES && acc > best.acc,
      notes: notes > best.notes,
    };
    const isRecord = records.streak || records.acc || records.notes;
    setRecap({ notes, correct: score, acc, time: secs, streakBest: sessionMaxStreak, records, isRecord });
    setPostedRank(null);
    if (isRecord) {
      fireConfetti();
      saveBadge({ difficulty, streak: sessionMaxStreak, accuracy: acc, notes, records });
    }
    if (onCommitPB) onCommitPB({ streak: sessionMaxStreak, acc, notes });
  };

  const postScore = async () => {
    const nm = lbName.trim();
    if (!nm) {
      toast.error("Enter a name for the leaderboard.");
      return;
    }
    try { localStorage.setItem("jt_trainer_name", nm); } catch (e) {}
    setPosting(true);
    try {
      const res = await onPostScore({ name: nm, streak: recap.streakBest, accuracy: recap.acc, notes: recap.notes });
      setPostedRank(res?.my_rank || 0);
      toast.success(res?.my_rank ? `You're #${res.my_rank} on this week's ${difficulty} board!` : "Added to the leaderboard!");
    } catch (e) {
      toast.error("Couldn't post right now. Please try again.");
    } finally {
      setPosting(false);
    }
  };

  const confirmReset = () => {
    setSecs(0);
    metRef.current = false;
    setRecap(null);
    if (onReset) onReset();
  };

  useEffect(() => {
    let effectiveStreak = streak;
    if (!metRef.current && secs >= goalMin * 60) {
      metRef.current = true;
      const y = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
      if (lastGoalDateRef.current === todayStr()) effectiveStreak = streak;
      else if (lastGoalDateRef.current === y) effectiveStreak = streak + 1;
      else effectiveStreak = 1;
      lastGoalDateRef.current = todayStr();
      setStreak(effectiveStreak);
      toast.success(`Daily practice goal reached — ${effectiveStreak}-day streak!`);
    }
    try {
      const existing = readPractice() || {};
      const history = { ...(existing.history || {}), [todayStr()]: secs };
      localStorage.setItem(
        "jt_practice",
        JSON.stringify({ date: todayStr(), secs, goalMin, streak: effectiveStreak, lastGoalDate: lastGoalDateRef.current, history }),
      );
    } catch (e) {}
  }, [secs, goalMin, streak]);

  const remainingToGoal = goalMin * 60 - secs;
  const nearGoal = remainingToGoal > 0 && remainingToGoal <= goalMin * 60 * 0.25;

  const pct = Math.min(100, Math.round((secs / (goalMin * 60)) * 100));

  return (
    <div className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="practice-timer">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Clock size={16} />
          </span>
          <div>
            <p className="font-serif text-lg">Practice session</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Today</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-sm text-primary" data-testid="practice-streak">
          <Flame size={14} /> {streak} day{streak === 1 ? "" : "s"}
        </div>
      </div>
      <div className="mt-4 flex items-end justify-between">
        <span className="font-serif text-3xl" data-testid="practice-elapsed">{fmtTime(secs)}</span>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">Goal {goalMin} min</span>
          <button
            onClick={resetTimer}
            data-testid="practice-timer-reset"
            title="Reset timer"
            className="flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary"
          >
            <RotateCcw size={13} /> Reset
          </button>
        </div>
      </div>
      {!running && (
        <p className="mt-2 text-xs text-muted-foreground" data-testid="practice-timer-idle">
          Answer your first note to start the timer.
        </p>
      )}
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-secondary">
        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} data-testid="practice-progress" />
      </div>
      {nearGoal && (
        <p className="mt-2 text-xs font-medium text-primary" data-testid="practice-reminder-near">
          Almost there — {Math.ceil(remainingToGoal / 60)} min to hit today's goal!
        </p>
      )}
      <div className="mt-4 flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Daily goal</span>
        <div className="inline-flex rounded-full border border-border bg-background p-1">
          {GOAL_OPTIONS.map((g) => (
            <button
              key={g}
              onClick={() => setGoalMin(g)}
              data-testid={`practice-goal-${g}`}
              className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                goalMin === g ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {g}m
            </button>
          ))}
        </div>
      </div>

      {recap && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" data-testid="session-recap" onClick={confirmReset}>
          <div className="w-full max-w-sm rounded-3xl border border-border bg-card p-8 text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <span className={`mx-auto flex h-14 w-14 items-center justify-center rounded-2xl ${recap.isRecord ? "bg-amber-400/15 text-amber-500" : "bg-primary/10 text-primary"}`}>
              {recap.isRecord ? <Trophy size={26} /> : <Zap size={26} />}
            </span>
            <h3 className="mt-5 font-serif text-2xl">Session recap</h3>
            {recap.isRecord ? (
              <div className="mx-auto mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 px-3 py-1 text-sm font-medium text-amber-600 dark:text-amber-400" data-testid="recap-new-best">
                <Trophy size={14} /> New personal best!
              </div>
            ) : (
              <p className="mt-1 text-sm text-muted-foreground">Nice work — here's how this session went.</p>
            )}
            <div className="mt-6 grid grid-cols-3 gap-3">
              <div className={`relative rounded-2xl p-4 ${recap.records.notes ? "bg-amber-400/10 ring-1 ring-amber-400/40" : "bg-background"}`} data-testid="recap-notes">
                {recap.records.notes && <Trophy size={12} className="absolute right-2 top-2 text-amber-500" />}
                <p className="font-serif text-2xl text-primary">{recap.notes}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Notes</p>
              </div>
              <div className={`relative rounded-2xl p-4 ${recap.records.acc ? "bg-amber-400/10 ring-1 ring-amber-400/40" : "bg-background"}`} data-testid="recap-accuracy">
                {recap.records.acc && <Trophy size={12} className="absolute right-2 top-2 text-amber-500" />}
                <p className="font-serif text-2xl text-primary">{recap.acc}%</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Accuracy</p>
              </div>
              <div className={`relative rounded-2xl p-4 ${recap.records.streak ? "bg-amber-400/10 ring-1 ring-amber-400/40" : "bg-background"}`} data-testid="recap-best-streak">
                {recap.records.streak && <Trophy size={12} className="absolute right-2 top-2 text-amber-500" />}
                <p className="font-serif text-2xl text-primary">{recap.streakBest}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Best streak</p>
              </div>
            </div>
            <p className="mt-3 text-xs text-muted-foreground" data-testid="recap-time">Practiced for {fmtTime(recap.time)}</p>
            {recap.isRecord && (
              <div className="mt-5 space-y-3 border-t border-border pt-4">
                <button
                  onClick={() => shareBadge({ difficulty, streak: recap.streakBest, accuracy: recap.acc, notes: recap.notes })}
                  data-testid="recap-share"
                  className="flex w-full items-center justify-center gap-2 rounded-full border border-border py-2 text-sm font-medium transition-colors hover:border-primary hover:text-primary"
                >
                  <Share2 size={15} /> Share your badge
                </button>
                {postedRank == null ? (
                  <div className="flex gap-2">
                    <input
                      value={lbName}
                      onChange={(e) => setLbName(e.target.value)}
                      placeholder="Your name"
                      maxLength={40}
                      data-testid="leaderboard-name"
                      className="flex-1 rounded-full border border-border bg-background px-4 py-2 text-sm outline-none focus:border-primary"
                    />
                    <Button onClick={postScore} disabled={posting} data-testid="leaderboard-submit" className="rounded-full bg-primary text-foreground hover:bg-primary/90">
                      {posting ? "Posting…" : "Post"}
                    </Button>
                  </div>
                ) : (
                  <p className="text-center text-sm text-primary" data-testid="leaderboard-posted">
                    {postedRank ? `You're #${postedRank} on this week's ${difficulty} board!` : "Added to this week's board!"}
                  </p>
                )}
                <p className="text-center text-[11px] text-muted-foreground">Optional — posts your best to this week's public leaderboard.</p>
              </div>
            )}
            <div className="mt-6 flex gap-3">
              <Button variant="outline" onClick={() => setRecap(null)} data-testid="recap-keep" className="flex-1 rounded-full border-border">Keep going</Button>
              <Button onClick={confirmReset} data-testid="recap-fresh" className="flex-1 rounded-full bg-primary text-foreground hover:bg-primary/90">Start fresh</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Trainer() {
  const [clef, setClef] = useState("treble");
  const [difficulty, setDifficulty] = useState("medium");
  const [current, setCurrent] = useState(randomPitch("treble", "medium"));
  const [feedback, setFeedback] = useState(null); // 'right' | 'wrong'
  const [chosen, setChosen] = useState(null);
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [started, setStarted] = useState(false);
  const [streak, setStreak] = useState(0);
  const [sessionMax, setSessionMax] = useState(0);
  const [pb, setPb] = useState(() => readPB());
  const [goals, setGoals] = useState(() => readGoals());
  const [boardRefresh, setBoardRefresh] = useState(0);
  const [noteStats, setNoteStats] = useState(() => readNoteStats());
  const milestoneRef = useRef(false);
  const goalHitRef = useRef({ streak: false, acc: false });
  const [bpm, setBpm] = useState(80);
  const [metroOn, setMetroOn] = useState(false);
  const [beats, setBeats] = useState(4);
  const [subdiv, setSubdiv] = useState(1);
  const [beatKey, setBeatKey] = useState(0);
  const [currentBeat, setCurrentBeat] = useState(-1);
  const [countingIn, setCountingIn] = useState(false);

  useEffect(() => () => stopMetronome(), []);

  const runMetro = (bpmV, beatsV, subV) => {
    setCountingIn(true);
    setCurrentBeat(-1);
    startMetronome(
      bpmV,
      beatsV,
      subV,
      (beatNum, inCountIn) => {
        setCurrentBeat(beatNum);
        setBeatKey((k) => k + 1);
        setCountingIn(inCountIn);
      },
      beatsV,
    );
  };

  const toggleMetro = () => {
    if (metroOn) {
      stopMetronome();
      setMetroOn(false);
      setCountingIn(false);
      setCurrentBeat(-1);
    } else {
      runMetro(bpm, beats, subdiv);
      setMetroOn(true);
    }
  };

  const changeBpm = (v) => {
    const val = Math.max(40, Math.min(208, Number(v) || 80));
    setBpm(val);
    if (metroOn) runMetro(val, beats, subdiv);
  };

  const changeBeats = (b) => {
    setBeats(b);
    if (metroOn) runMetro(bpm, b, subdiv);
  };

  const changeSubdiv = (s) => {
    setSubdiv(s);
    if (metroOn) runMetro(bpm, beats, s);
  };

  const nextNote = useCallback((c, d) => {
    setCurrent(randomPitch(c || clef, d || difficulty));
    setFeedback(null);
    setChosen(null);
  }, [clef, difficulty]);

  const switchClef = (c) => {
    setClef(c);
    nextNote(c);
  };

  const commitPB = useCallback((session) => {
    setPb((prev) => {
      const cur = pbFor(prev, difficulty);
      const updated = {
        streak: Math.max(cur.streak, session.streak || 0),
        acc: (session.notes || 0) >= PB_MIN_NOTES ? Math.max(cur.acc, session.acc || 0) : cur.acc,
        notes: Math.max(cur.notes, session.notes || 0),
      };
      const next = { ...prev, [difficulty]: updated };
      try {
        localStorage.setItem("jt_trainer_pb", JSON.stringify(next));
      } catch (e) {}
      return next;
    });
  }, [difficulty]);

  const changeDifficulty = (d) => {
    commitPB({ streak: sessionMax, acc: attempts ? Math.round((score / attempts) * 100) : 0, notes: attempts });
    setDifficulty(d);
    setScore(0);
    setAttempts(0);
    setStreak(0);
    setSessionMax(0);
    milestoneRef.current = false;
    goalHitRef.current = { streak: false, acc: false };
    nextNote(clef, d);
  };

  const setGoal = (field, value) => {
    setGoals((prev) => {
      const cur = goalsFor(prev, difficulty);
      const next = { ...prev, [difficulty]: { ...cur, [field]: value } };
      try {
        localStorage.setItem("jt_trainer_goals", JSON.stringify(next));
      } catch (e) {}
      return next;
    });
    goalHitRef.current = { ...goalHitRef.current, [field]: false };
  };

  const postScore = async (payload) => {
    const res = await api.post("/trainer/leaderboard", { difficulty, ...payload });
    setBoardRefresh((k) => k + 1);
    return res.data;
  };

  const guess = (letter) => {
    if (feedback) return;
    const correct = letterOf(current);
    const isRight = letter === correct;
    const newAttempts = attempts + 1;
    const newScore = score + (isRight ? 1 : 0);
    const newStreak = isRight ? streak + 1 : 0;
    setChosen(letter);
    setAttempts(newAttempts);
    setScore(newScore);
    setStreak(newStreak);
    setStarted(true);
    setNoteStats(recordNote(difficulty, clef, current, isRight));
    if (isRight) {
      setFeedback("right");
      playRight();
      if (newStreak > sessionMax) setSessionMax(newStreak);
      const bestStreak = pbFor(pb, difficulty).streak;
      if (!milestoneRef.current && bestStreak > 0 && newStreak > bestStreak) {
        milestoneRef.current = true;
        toast.success(`New best streak — ${newStreak} in a row! 🔥`);
      }
      setTimeout(() => playPitch(current), 280);
    } else {
      setFeedback("wrong");
      playWrong();
    }
    const g = goalsFor(goals, difficulty);
    if (g.streak > 0 && !goalHitRef.current.streak && newStreak >= g.streak) {
      goalHitRef.current.streak = true;
      fireConfetti();
      toast.success(`Streak goal reached — ${g.streak} in a row! 🎯`);
    }
    const acc = newAttempts ? Math.round((newScore / newAttempts) * 100) : 0;
    if (g.acc > 0 && !goalHitRef.current.acc && newAttempts >= 5 && acc >= g.acc) {
      goalHitRef.current.acc = true;
      fireConfetti();
      toast.success(`Accuracy goal reached — ${acc}%! 🎯`);
    }
  };

  const reset = () => {
    commitPB({ streak: sessionMax, acc: attempts ? Math.round((score / attempts) * 100) : 0, notes: attempts });
    setScore(0);
    setAttempts(0);
    setStreak(0);
    setSessionMax(0);
    milestoneRef.current = false;
    goalHitRef.current = { streak: false, acc: false };
    nextNote();
  };

  useEffect(() => {
    if (feedback === "right") {
      const t = setTimeout(() => nextNote(), 900);
      return () => clearTimeout(t);
    }
  }, [feedback, nextNote]);

  const accuracy = attempts ? Math.round((score / attempts) * 100) : 0;

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="mx-auto max-w-4xl px-4 sm:px-8 py-10 lg:py-14">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Endless practice</p>
        <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl">Sight-Reading Trainer</h1>
        <SoundToggle testId="trainer-sound-toggle" className="mt-3" />
        <p className="mt-4 max-w-xl text-muted-foreground">
          A note appears on the staff — name it as fast as you can. Correct answers play the pitch so your eyes and ears learn together.
        </p>

        {/* clef toggle + stats */}
        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded-full border border-border bg-card p-1">
              {["treble", "bass"].map((c) => (
                <button
                  key={c}
                  onClick={() => switchClef(c)}
                  data-testid={`trainer-clef-${c}`}
                  className={`rounded-full px-5 py-2 text-sm font-medium capitalize transition-colors ${
                    clef === c ? "bg-primary text-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {c} clef
                </button>
              ))}
            </div>
            <div className="inline-flex rounded-full border border-border bg-card p-1" data-testid="trainer-difficulty">
              {[{ v: "easy", l: "Easy" }, { v: "medium", l: "Medium" }, { v: "hard", l: "Hard" }].map((d) => (
                <button
                  key={d.v}
                  onClick={() => changeDifficulty(d.v)}
                  data-testid={`trainer-difficulty-${d.v}`}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    difficulty === d.v ? "bg-primary text-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {d.l}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-5 text-sm">
            <div className="text-center">
              <p className="font-serif text-2xl text-foreground" data-testid="trainer-score">{score}</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Correct</p>
            </div>
            <div className="text-center">
              <p className="font-serif text-2xl text-primary">{accuracy}%</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Accuracy</p>
            </div>
            <div className="text-center">
              <p className="flex items-center gap-1 font-serif text-2xl text-foreground">
                <Zap size={16} className="text-primary" />{streak}
              </p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Streak</p>
            </div>
          </div>
        </div>

        <PracticeTimer
          running={started}
          attempts={attempts}
          score={score}
          difficulty={difficulty}
          sessionMaxStreak={sessionMax}
          pb={pb}
          onCommitPB={commitPB}
          onPostScore={postScore}
          onReset={() => {
            setScore(0);
            setAttempts(0);
            setStreak(0);
            setSessionMax(0);
            milestoneRef.current = false;
            goalHitRef.current = { streak: false, acc: false };
            setStarted(false);
            nextNote();
          }}
        />

        {(() => {
          const best = pbFor(pb, difficulty);
          const g = goalsFor(goals, difficulty);
          return (
            <div className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="personal-best">
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-400/15 text-amber-500">
                  <Trophy size={16} />
                </span>
                <div>
                  <p className="font-serif text-lg">Personal best</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground capitalize">{difficulty} level</p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div className="rounded-2xl bg-background p-4 text-center" data-testid="pb-streak">
                  <p className="flex items-center justify-center gap-1 font-serif text-2xl text-foreground">
                    <Zap size={15} className="text-primary" />{best.streak}
                  </p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Best streak</p>
                </div>
                <div className="rounded-2xl bg-background p-4 text-center" data-testid="pb-accuracy">
                  <p className="font-serif text-2xl text-foreground">{best.acc}%</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Best accuracy</p>
                </div>
                <div className="rounded-2xl bg-background p-4 text-center" data-testid="pb-notes">
                  <p className="font-serif text-2xl text-foreground">{best.notes}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Most notes</p>
                </div>
              </div>
              <div className="mt-4 border-t border-border pt-4">
                <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  <Target size={12} className="text-primary" /> Set a practice goal
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Streak</span>
                    <div className="inline-flex rounded-full border border-border bg-background p-1">
                      {[0, 5, 10, 20].map((v) => (
                        <button
                          key={v}
                          onClick={() => setGoal("streak", v)}
                          data-testid={`goal-streak-${v}`}
                          className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${g.streak === v ? "bg-primary text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                        >
                          {v === 0 ? "Off" : v}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Accuracy</span>
                    <div className="inline-flex rounded-full border border-border bg-background p-1">
                      {[0, 80, 90, 100].map((v) => (
                        <button
                          key={v}
                          onClick={() => setGoal("acc", v)}
                          data-testid={`goal-acc-${v}`}
                          className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${g.acc === v ? "bg-primary text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                        >
                          {v === 0 ? "Off" : `${v}%`}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        <NoteAccuracyMap stats={noteStats} clef={clef} difficulty={difficulty} />

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <Link to="/daily-challenge" data-testid="link-daily-challenge" className="group flex items-center gap-4 rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><CalendarDays size={18} /></span>
            <div className="flex-1">
              <p className="font-serif text-xl">Daily Challenge</p>
              <p className="text-sm text-muted-foreground">Today's 10 notes — same for everyone. Race the clock.</p>
            </div>
            <ChevronRight size={18} className="text-muted-foreground transition-transform group-hover:translate-x-1" />
          </Link>
          <Link to="/badges" data-testid="link-badge-gallery" className="group flex items-center gap-4 rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-400/15 text-amber-500"><Images size={18} /></span>
            <div className="flex-1">
              <p className="font-serif text-xl">Badge Gallery</p>
              <p className="text-sm text-muted-foreground">Every personal best you've earned, ready to re-download.</p>
            </div>
            <ChevronRight size={18} className="text-muted-foreground transition-transform group-hover:translate-x-1" />
          </Link>
        </div>

        <TrainerLeaderboard activeDifficulty={difficulty} refreshKey={boardRefresh} />

        {/* metronome */}
        <div className="mt-6 flex flex-col gap-4 rounded-2xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="trainer-metronome">
          <div className="flex items-center gap-3">
            <button
              onClick={toggleMetro}
              data-testid="trainer-metronome-toggle"
              className={`flex h-11 w-11 items-center justify-center rounded-full transition-colors ${
                metroOn ? "bg-primary text-foreground" : "bg-secondary text-primary hover:bg-muted"
              }`}
              aria-label={metroOn ? "Stop metronome" : "Start metronome"}
            >
              {metroOn ? <Pause size={18} /> : <Play size={18} />}
            </button>
            <div>
              <p className="flex items-center gap-1.5 font-serif text-lg text-foreground">
                <Timer size={16} className="text-primary" /> Metronome
              </p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {metroOn ? "Ticking" : "Keep a steady pulse"}
              </p>
            </div>
            <span
              aria-hidden
              data-testid="trainer-pulse"
              className="ml-1 h-3.5 w-3.5 rounded-full bg-primary shadow-[0_0_14px_hsl(var(--primary))]"
              style={{ animation: metroOn ? `beatpulse ${(60 / bpm).toFixed(3)}s ease-in-out infinite` : "none", opacity: metroOn ? 1 : 0.2 }}
            />
          </div>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="40"
              max="208"
              step="1"
              value={bpm}
              onChange={(e) => changeBpm(e.target.value)}
              data-testid="trainer-bpm-slider"
              className="h-2 w-40 cursor-pointer appearance-none rounded-full bg-secondary accent-primary"
            />
            <div className="text-center">
              <p className="font-serif text-2xl text-foreground" data-testid="trainer-bpm-value">{bpm}</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">BPM</p>
            </div>
          </div>
        </div>

        {/* time signature + subdivision */}
        <div className="mt-3 flex flex-wrap items-center gap-x-8 gap-y-3 rounded-2xl border border-border bg-card px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Beats</span>
            <div className="inline-flex rounded-full border border-border bg-background p-1">
              {[2, 3, 4, 6].map((b) => (
                <button
                  key={b}
                  onClick={() => changeBeats(b)}
                  data-testid={`trainer-beats-${b}`}
                  className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                    beats === b ? "bg-primary text-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {b}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Feel</span>
            <div className="inline-flex rounded-full border border-border bg-background p-1">
              {[{ v: 1, l: "Quarter" }, { v: 2, l: "Eighth" }, { v: 3, l: "Triplet" }].map((s) => (
                <button
                  key={s.v}
                  onClick={() => changeSubdiv(s.v)}
                  data-testid={`trainer-subdiv-${s.l.toLowerCase()}`}
                  className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                    subdiv === s.v ? "bg-primary text-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {s.l}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* staff card */}
        <motion.div
          key={current + clef}
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className={`relative mt-6 overflow-hidden rounded-3xl border-2 bg-card p-6 sm:p-10 transition-colors ${
            feedback === "right" ? "border-emerald-500" : feedback === "wrong" ? "border-red-500" : "border-border"
          }`}
          data-testid="trainer-card"
        >
          {metroOn && (
            <motion.div
              key={beatKey}
              initial={{ opacity: 0.22 }}
              animate={{ opacity: 0 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="pointer-events-none absolute inset-0 bg-primary"
              aria-hidden
            />
          )}
          {metroOn && countingIn && currentBeat >= 0 && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center" data-testid="trainer-countin">
              <span className="font-serif text-8xl font-bold text-primary drop-shadow-lg">{currentBeat + 1}</span>
            </div>
          )}
          <StaffViewer clef={clef} notes={[{ pitch: current }]} width={420} playOnClick={true} testId="trainer-staff" />
          <div className="mt-2 flex justify-center">
            <button
              onClick={() => playPitch(current)}
              data-testid="trainer-hear"
              className="flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-sm text-primary hover:bg-primary/20"
            >
              <Volume2 size={15} /> Hear the note
            </button>
          </div>

          {feedback && (
            <p
              className={`mt-4 text-center font-serif text-xl ${feedback === "right" ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}
              data-testid="trainer-feedback"
            >
              {feedback === "right" ? (
                <span className="inline-flex items-center gap-2"><Check size={20} /> Correct — that's {letterOf(current)}!</span>
              ) : (
                <span className="inline-flex items-center gap-2"><X size={20} /> It was {letterOf(current)}. Keep going!</span>
              )}
            </p>
          )}
        </motion.div>

        {/* answer keys */}
        <div className="mt-6 grid grid-cols-4 gap-3 sm:grid-cols-7">
          {LETTERS.map((l) => {
            const correct = letterOf(current);
            let cls = "border-border bg-card hover:border-primary";
            if (feedback && l === correct) cls = "border-emerald-500 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400";
            else if (feedback && l === chosen) cls = "border-red-500 bg-red-500/15 text-red-600 dark:text-red-400";
            return (
              <button
                key={l}
                onClick={() => guess(l)}
                disabled={!!feedback}
                data-testid={`trainer-answer-${l}`}
                className={`rounded-xl border-2 py-4 font-serif text-2xl transition-all ${cls}`}
              >
                {l}
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex justify-center gap-3">
          {feedback === "wrong" && (
            <Button onClick={() => nextNote()} data-testid="trainer-next" className="rounded-full bg-primary text-foreground hover:bg-primary/90">
              Next note
            </Button>
          )}
          <Button onClick={reset} variant="outline" data-testid="trainer-reset" className="rounded-full border-border">
            <RotateCcw size={15} className="mr-2" /> Reset session
          </Button>
        </div>
      </div>
    </div>
  );
}
